class ProductUpsellVariantSelector extends HTMLElement {
  constructor() {
    super();
    
    // Only initialize if we're in a product-upsell-variant-selector context and NOT in a cart-drawer
    if (this.closest('cart-drawer')) {
      // We're in a cart drawer, don't initialize this component
      return;
    }
    
    // Get DOM elements
    this.form = this.querySelector('form');
    this.variantIdInput = this.querySelector('input[name="id"]');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.jsonData = this.querySelector('script[type="application/json"]');
    
    // Initialize product data
    this.product = JSON.parse(this.dataset.product || '{}');
    
    // Find the variant selectors - we need to look for the closest parent
    const upsellItem = this.closest('.product-upsell-item');
    
    if (upsellItem) {
      // Find the variant selectors within the upsell item
      this.variantSelects = upsellItem.querySelector('.product-upsell-item__variants');
      
      if (this.variantSelects) {
        // Handle dropdown selects
        this.selectElements = this.variantSelects.querySelectorAll('select');
        this.selectElements.forEach(select => {
          select.addEventListener('change', this.handleSelectChange.bind(this));
        });
        
        // Handle radio buttons for pills and swatches
        this.radioElements = this.variantSelects.querySelectorAll('input[type="radio"]');
        if (this.radioElements.length > 0) {
          // Add event listeners to all radio buttons
          this.radioElements.forEach(radio => {
            // Remove any existing listeners to avoid duplicates
            radio.removeEventListener('change', this.handleSelectChange.bind(this));
            // Add the new listener
            radio.addEventListener('change', this.handleSelectChange.bind(this));
            // Also add click listener as a backup
            radio.addEventListener('click', this.handleSelectChange.bind(this));
          });
        }
      }
    }
    
    // Set up form submission
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }
  
  handleSelectChange(event) {
    // Handle dropdown selects - these use variant IDs directly
    if (this.selectElements && this.selectElements.length > 0) {
      // For dropdowns, we use the variant ID directly
      const variantId = this.selectElements[0].value;
      if (variantId) {
        // Find the variant by ID and update
        const variant = this.getVariantData().find(v => v.id.toString() === variantId.toString());
        if (variant) {
          this.updateId(variant.id);
          
          // Update button state
          if (this.submitButton) {
            if (!variant.available) {
              this.submitButton.setAttribute('disabled', 'disabled');
            } else {
              this.submitButton.removeAttribute('disabled');
            }
          }
          
          // Update variant image if available
          if (variant.featured_image) {
            const upsellItem = this.closest('.product-upsell-item');
            if (upsellItem) {
              const productImage = upsellItem.querySelector('.product-upsell-item__image img');
              if (productImage) {
                productImage.src = variant.featured_image.src;
              }
            }
          }
        }
        return; // Exit early since we've handled the dropdown case
      }
    } 
    
    // Handle radio buttons (pills and swatches)
    if (this.radioElements && this.radioElements.length > 0) {
      // Group radio buttons by option name
      const optionValues = {};
      const checkedRadios = Array.from(this.radioElements).filter(radio => radio.checked);
      
      // If we don't have any checked radios, exit
      if (checkedRadios.length === 0) return;
      
      // Get the selected values for each option
      checkedRadios.forEach(radio => {
        optionValues[radio.name] = radio.value;
      });
      
      // Get all product options
      const productOptions = this.product.options;
      
      // Find the matching variant
      const variants = this.getVariantData();
      const matchingVariant = variants.find(variant => {
        // Check if all selected options match this variant
        return productOptions.every((option, index) => {
          const optionName = option;
          const variantOptionValue = variant.options[index];
          const selectedValue = optionValues[optionName];
          
          // If we don't have a selection for this option, consider it a match
          if (!selectedValue) return true;
          
          // Otherwise, check if the values match
          return variantOptionValue === selectedValue;
        });
      });
      
      // If we found a matching variant, update the form
      if (matchingVariant) {
        this.updateId(matchingVariant.id);
        
        // Update button state
        if (this.submitButton) {
          if (!matchingVariant.available) {
            this.submitButton.setAttribute('disabled', 'disabled');
          } else {
            this.submitButton.removeAttribute('disabled');
          }
        }
        
        // Update variant image if available
        if (matchingVariant.featured_image) {
          const upsellItem = this.closest('.product-upsell-item');
          if (upsellItem) {
            const productImage = upsellItem.querySelector('.product-upsell-item__image img');
            if (productImage) {
              productImage.src = matchingVariant.featured_image.src;
            }
          }
        }
      }
      return; // Exit early since we've handled the radio button case
    }
  }
  
  updateId(variantId) {
    if (this.variantIdInput) {
      this.variantIdInput.value = variantId;
    }
  }
  
  getVariantData() {
    if (!this.variantData && this.jsonData) {
      this.variantData = JSON.parse(this.jsonData.textContent);
    }
    return this.variantData || [];
  }
  
  onSubmit(event) {
    // If the form has data-ajax attribute, prevent default and handle via AJAX
    if (this.form.dataset.ajax) {
      event.preventDefault();
      
      // Get form data
      const formData = new FormData(this.form);
      
      // Show loading spinner
      const button = this.form.querySelector('button[type="submit"]');
      const spinner = button.querySelector('.loading-overlay__spinner');
      
      if (spinner) {
        spinner.classList.remove('hidden');
      }
      
      // Add loading class to button
      button.classList.add('loading');
      
      // Disable button
      button.setAttribute('disabled', 'disabled');
      
      // Add to cart via AJAX
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        // Hide spinner
        if (spinner) {
          spinner.classList.add('hidden');
        }
        
        // Remove loading class from button
        button.classList.remove('loading');
        
        // Re-enable button
        button.removeAttribute('disabled');
        
        // Update cart count
        this.updateCartCount();
        
        // Refresh cart drawer if available
        // this.refreshCartDrawer();
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        
        // Hide spinner
        if (spinner) {
          spinner.classList.add('hidden');
        }
        
        // Remove loading class from button
        button.classList.remove('loading');
        
        // Re-enable button
        button.removeAttribute('disabled');
      });
    }
  }
  
  refreshCartDrawer() {
    // Try to find cart drawer component
    const cartDrawerComponent = document.querySelector('cart-drawer');

    if (cartDrawerComponent) {
      // Trigger cart update event to refresh cart items without full page reload
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'upsell-variant-selector'
        });
      } else {
        // Fallback: dispatch custom event if PUB_SUB_EVENTS not available
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } else {
      // Try to open cart drawer if it's not visible
      const cartDrawerToggle = document.querySelector('[data-action="open-drawer"][data-drawer="cart"]');
      if (cartDrawerToggle) {
        cartDrawerToggle.click();
      }
    }
  }
  
  updateCartCount() {
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
        });
        
        const cartCountAccessibleElements = document.querySelectorAll('.cart-count-bubble span:not([aria-hidden="true"])');
        cartCountAccessibleElements.forEach(el => {
          el.textContent = cart.item_count === 1 
            ? window.accessibilityStrings.cartSingularAccessibilityString 
            : window.accessibilityStrings.cartPluralAccessibilityString.replace('[count]', cart.item_count);
        });
      });
  }
}

customElements.define('product-upsell-variant-selector', ProductUpsellVariantSelector);
